import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useRBAC } from './RBACContext';

/**
 * ProfileContext — Manages active profile (superMode) + centralized admin check.
 *
 * Provides:
 *  - isSuperAdmin: boolean (role='super_admin' OR email in GOVERNANCE_ADMIN_EMAILS)
 *  - superMode: boolean (admin-only toggle to show all Arena sections)
 *  - toggleSuperMode: () => void
 *  - hasCapability: (cap: string) => boolean
 *
 * superMode controls visibility of adminOnly nav sections (Governance, etc.).
 * hasCapability returns true for super admins regardless of superMode.
 *
 * Capabilities are loaded from RBACContext permissions (GET /api/me response).
 * ProfileProvider MUST be nested inside RBACProvider (see App.js provider order).
 */

const ADMIN_EMAILS = (process.env.REACT_APP_GOVERNANCE_ADMIN_EMAILS || '')
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

const STORAGE_KEY_SUPER = 'vukz_super_mode';
// Migrate legacy key on load
try {
  if (!localStorage.getItem('vukz_super_mode') && localStorage.getItem('sucron_super_mode')) {
    localStorage.setItem('vukz_super_mode', localStorage.getItem('sucron_super_mode'));
    localStorage.removeItem('sucron_super_mode');
  }
} catch { /* ignore */ }

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

/**
 * Centralized super admin check.
 * Use this everywhere instead of duplicating ADMIN_EMAILS logic.
 */
export const checkIsSuperAdmin = (user) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  if (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())) return true;
  return false;
};

export const ProfileProvider = ({ children }) => {
  const { user } = useAuth();
  const { permissions: rbacPermissions } = useRBAC();

  const isSuperAdmin = useMemo(() => checkIsSuperAdmin(user), [user]);

  // Super Mode — persisted. Default ON for admins (backward compat).
  const [superMode, setSuperModeState] = useState(() => {
    if (!isSuperAdmin) return false;
    try {
      const saved = localStorage.getItem(STORAGE_KEY_SUPER);
      if (saved !== null) return saved === 'true';
      return true; // Default ON so existing admin sees governance
    } catch {
      return true;
    }
  });

  const toggleSuperMode = useCallback(
    (val) => {
      if (!isSuperAdmin) return;
      const next = typeof val === 'boolean' ? val : !superMode;
      setSuperModeState(next);
      try {
        localStorage.setItem(STORAGE_KEY_SUPER, String(next));
      } catch {
        // ignore
      }
    },
    [isSuperAdmin, superMode],
  );

  // ──────────────────────────────────────────────────────
  // Capabilities — sourced from RBACContext permissions (GET /api/me).
  // Super admin always returns true for any capability.
  // ──────────────────────────────────────────────────────
  const capabilities = useMemo(() => rbacPermissions || [], [rbacPermissions]);

  const hasCapability = useCallback(
    (cap) => {
      if (isSuperAdmin) return true; // super admin has all capabilities
      return capabilities.includes(cap);
    },
    [capabilities, isSuperAdmin],
  );

  const value = useMemo(
    () => ({
      isSuperAdmin,
      superMode: isSuperAdmin ? superMode : false,
      toggleSuperMode,
      hasCapability,
      capabilities,
    }),
    [isSuperAdmin, superMode, toggleSuperMode, hasCapability, capabilities],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};
